// AI-GENERATED | 365 Copilot (GPT-5) | Tarkistanut Oskari Rintamäki | 2026-04-15
// Shared kebab (⋯) actions menu for GFI features.
// Used by card view (FeatureDataTabContentItem)
// and table view (FeatureDataTabContent).

import { useEffect, useRef, useState, useId } from 'react';
import { createPortal } from 'react-dom';
import styled from 'styled-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEllipsis } from '@fortawesome/free-solid-svg-icons';
import { Tooltip } from 'react-tooltip';
import { isMobile, theme } from '../../../theme/theme';
import strings from '../../../translations';
import { useAppSelector } from '../../../state/hooks';
import { Slide, toast } from 'react-toastify';

const MENU_WIDTH = 220;
const VIEWPORT_PADDING = 8;

const MenuButton = styled.button`
  border: none;
  background: transparent;
  cursor: pointer;
  color: ${(p) => p.theme.colors.mainColor1};
  font-size: 18px;
`;

const PortalMenu = styled.div`
  position: fixed;
  min-width: ${MENU_WIDTH}px;
  background: ${(p) => p.theme.colors.mainWhite};
  border: 1px solid ${(p) => p.theme.colors.lightGrey};
  border-radius: 10px;
  box-shadow: 0 6px 18px rgba(0, 0, 0, 0.15);
  z-index: 10000;
  overflow: hidden;
`;

const MenuItemLink = styled.a`
  display: block;
  padding: 10px 12px;
  color: ${(p) => p.theme.colors.mainColor1};
  text-decoration: none;
  font-size: 14px;

  &:hover {
    background-color: ${(p) => p.theme.colors.hover};
  }
`;

const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

const FeatureActionsMenu = ({
  data,
  reports = null,
  ariaLabel = 'Actions',
  disableTooltip = false
}) => {
  const { channel } = useAppSelector((state) => state.rpc);

  const [open, setOpen] = useState(false);
  const [actions, setActions] = useState([]);
  const btnRef = useRef(null);
  const menuRef = useRef(null);
  const [pos, setPos] = useState({ top: 0, left: 0 });

  const reactId = useId();

  const tooltipId = `actions-menu-tooltip-${reactId}`;

  /**
   * FAILSAFE:
   * If reports is null or empty, we intentionally render nothing.
   */
  const hasReports = Array.isArray(reports) && reports.length > 0;

  const openMenu = () => {
    const rect = btnRef.current?.getBoundingClientRect();
    if (!rect) return;

    const left = clamp(
      rect.right - MENU_WIDTH,
      VIEWPORT_PADDING,
      window.innerWidth - MENU_WIDTH - VIEWPORT_PADDING
    );

    const top = clamp(
      rect.bottom + 6,
      VIEWPORT_PADDING,
      window.innerHeight - VIEWPORT_PADDING
    );

    setPos({ top, left });
    setOpen(true);
  };

  useEffect(() => {
    if (!open) return;

    const onPointerDownCapture = (e) => {
      if (
        menuRef.current?.contains(e.target) ||
        btnRef.current?.contains(e.target)
      ) {
        return;
      }
      setOpen(false);
    };

    const onKeyDown = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };

    document.addEventListener('pointerdown', onPointerDownCapture, true);
    document.addEventListener('keydown', onKeyDown);

    return () => {
      document.removeEventListener('pointerdown', onPointerDownCapture, true);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  const downloadReport = (reportType, value) => {
    channel.getReport(
      [reportType, value],
      (result) => {
       // Download handled inside RPC function
      },
      (error) => {
        // Error on fetching report
        console.error(error);
        toast.error(
          'Virhe selosteen/raportin latauksessa:' + error,
          {
            position: 'top-center',
            autoClose: 5000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
            progress: undefined,
            theme: 'colored',
            transition: Slide
          }
        );
      }
    );
  };

  useEffect(() => {
    if (!hasReports || !data?.keyValueProperties) {
      setActions([]);
      return;
    }

    const kvp = data.keyValueProperties;
    const resolvedActions = [];

    reports.forEach((reportDef) => {
      Object.entries(reportDef).forEach(([reportType, attributeKey]) => {
        const value = kvp?.[attributeKey];

        if (!value || !reportType) return;

        resolvedActions.push({
          id: `${reportType}-${attributeKey}`,
          label: strings.gfi.actions.reports?.[reportType] ?? reportType,
          reportType,
          value
        });
      });
    });

    setActions(resolvedActions);
  }, [data, reports, hasReports, channel]);

  if (!hasReports || actions.length === 0) {
    return null;
  }

  return (
    <>
      <Tooltip
        id={tooltipId}
        style={{ backgroundColor: theme.colors.mainColor1 }}
        hidden={isMobile || disableTooltip || open}
        place="bottom"
        effect="float"
      >
        {strings.gfi.actions.title}
      </Tooltip>

      <MenuButton
        ref={btnRef}
        type="button"
        aria-label={ariaLabel}
        aria-haspopup="menu"
        aria-expanded={open}
        data-tooltip-id={tooltipId}
        onClick={(e) => {
          e.stopPropagation();
          open ? setOpen(false) : openMenu();
        }}
      >
        <FontAwesomeIcon icon={faEllipsis} />
      </MenuButton>

      {open &&
        createPortal(
          <PortalMenu
            ref={menuRef}
            role="menu"
            style={{ top: pos.top, left: pos.left }}
            onClick={(e) => e.stopPropagation()}
          >
            {actions.map((action) => (
              <MenuItemLink
                key={action.id}
                onClick={() => downloadReport(action.reportType, action.value)}
                target="_blank"
                rel="noopener noreferrer"
              >
                {action.label}
              </MenuItemLink>
            ))}
          </PortalMenu>,
          document.body
        )}
    </>
  );
};

export default FeatureActionsMenu;
